import { AlertCircle, BarChart3, TrendingDown, TrendingUp, Users, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface DashboardIndicatorsProps {
  summary: {
    moyenneMasseSalarialeBrute: number;
    moyenneCoutGlobalEmployeur: number;
    moyenneTauxAbsenteisme: number;
    moyenneEcartRemunerationPct: number;
  };
  previousPeriodSummary?: {
    moyenneMasseSalarialeBrute: number;
    moyenneCoutGlobalEmployeur: number;
    moyenneTauxAbsenteisme: number;
    moyenneEcartRemunerationPct: number;
  };
  lastMonth: {
    chargesPatronalesNettes: number | null;
  };
  evolutions: {
    masseSalariale: number;
    coutGlobal: number;
    absenteisme: number;
    ecartRemuneration: number;
  };
  formatCurrency: (value: number) => string;
  formatPercent: (value: number) => string;
}

const EvolutionBadge = ({ value }: { value: number }) => {
  const isPositive = value >= 0;
  const Icon = isPositive ? TrendingUp : TrendingDown;
  return (
    <div
      className={`flex items-center gap-1 rounded-full px-2 py-1 text-xs ${
        isPositive
          ? 'bg-success-50 text-success-700'
          : 'bg-error-50 text-error-700'
      }`}
    >
      <Icon className="h-3 w-3" />
      <span>
        {isPositive ? '+' : ''}
        {value.toFixed(2)}%
      </span>
    </div>
  );
};

export function DashboardIndicators({
  summary,
  previousPeriodSummary,
  lastMonth,
  evolutions,
  formatCurrency,
  formatPercent,
}: DashboardIndicatorsProps) {
  const { t } = useTranslation();

  return (
    <div className="grid gap-4 md:grid-cols-5">
      {/* 1. Masse salariale brute */}
      <div className="rounded-xl border border-neutral-200 bg-white p-6">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
            <Wallet className="h-5 w-5 text-blue-600" />
          </div>
          <EvolutionBadge value={evolutions.masseSalariale} />
        </div>
        <h3 className="mb-1 text-sm font-medium text-neutral-600">
          {t('dashboardRh.indicators.masseSalariale', 'Masse salariale brute')}
        </h3>
        <p className="text-2xl font-bold text-neutral-900">
          {formatCurrency(summary.moyenneMasseSalarialeBrute)}
        </p>
        {previousPeriodSummary && (
          <p className="mt-1 text-xs text-neutral-500">
            {t('dashboardRh.comparison.previous', 'Période précédente')}:{' '}
            {formatCurrency(previousPeriodSummary.moyenneMasseSalarialeBrute)}
          </p>
        )}
      </div>

      {/* 2. Coût global employeur */}
      <div className="rounded-xl border border-neutral-200 bg-white p-6">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-50">
            <BarChart3 className="h-5 w-5 text-purple-600" />
          </div>
          <EvolutionBadge value={evolutions.coutGlobal} />
        </div>
        <h3 className="mb-1 text-sm font-medium text-neutral-600">
          {t('dashboardRh.indicators.coutGlobal', 'Coût global employeur')}
        </h3>
        <p className="text-2xl font-bold text-neutral-900">
          {formatCurrency(summary.moyenneCoutGlobalEmployeur)}
        </p>
        {previousPeriodSummary && (
          <p className="mt-1 text-xs text-neutral-500">
            {t('dashboardRh.comparison.previous', 'Période précédente')}:{' '}
            {formatCurrency(previousPeriodSummary.moyenneCoutGlobalEmployeur)}
          </p>
        )}
      </div>

      {/* 3. Charges patronales */}
      <div className="rounded-xl border border-neutral-200 bg-white p-6">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-50">
            <Wallet className="h-5 w-5 text-orange-600" />
          </div>
        </div>
        <h3 className="mb-1 text-sm font-medium text-neutral-600">
          {t('dashboardRh.indicators.chargesPatronales', 'Charges patronales')}
        </h3>
        <p className="text-2xl font-bold text-neutral-900">
          {formatCurrency(lastMonth.chargesPatronalesNettes || 0)}
        </p>
      </div>

      {/* 4. Taux d'absentéisme */}
      <div className="rounded-xl border border-neutral-200 bg-white p-6">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50">
            <AlertCircle className="h-5 w-5 text-red-600" />
          </div>
          <EvolutionBadge value={evolutions.absenteisme} />
        </div>
        <h3 className="mb-1 text-sm font-medium text-neutral-600">
          {t('dashboardRh.indicators.absenteisme', "Taux d'absentéisme")}
        </h3>
        <p className="text-2xl font-bold text-neutral-900">
          {formatPercent(summary.moyenneTauxAbsenteisme)}
        </p>
        {previousPeriodSummary && (
          <p className="mt-1 text-xs text-neutral-500">
            {t('dashboardRh.comparison.previous', 'Période précédente')}:{' '}
            {formatPercent(previousPeriodSummary.moyenneTauxAbsenteisme)}
          </p>
        )}
      </div>

      {/* 5. Égalité H/F */}
      <div className="rounded-xl border border-neutral-200 bg-white p-6">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-50">
            <Users className="h-5 w-5 text-green-600" />
          </div>
          <EvolutionBadge value={evolutions.ecartRemuneration} />
        </div>
        <h3 className="mb-1 text-sm font-medium text-neutral-600">
          {t('dashboardRh.indicators.egaliteHF', 'Écart rémunération H/F')}
        </h3>
        <p className="text-2xl font-bold text-neutral-900">
          {formatPercent(summary.moyenneEcartRemunerationPct)}
        </p>
        {previousPeriodSummary && (
          <p className="mt-1 text-xs text-neutral-500">
            {t('dashboardRh.comparison.previous', 'Période précédente')}:{' '}
            {formatPercent(previousPeriodSummary.moyenneEcartRemunerationPct)}
          </p>
        )}
      </div>
    </div>
  );
}

